import { addMinutes } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";
import { Card, Icon, Tooltip, Typography } from "@/components/atoms";
import { useBookingETA } from "../../BookingTripMap/hooks/useBookingETA";
import { useBookingDriverActivityContext } from "../context";

export const BookingInformation = () => {
  const { tripLog } = useBookingDriverActivityContext();
  const { ETA } = useBookingETA({ tripLog });

  const { job, bookingStops, driverCurrentLocationData } = tripLog;
  const { id, pickupDate, timezone, flightNumber, driver } = job;
  const { pickupLocationData, dropoffLocationData } = bookingStops;
  if (!pickupLocationData || !dropoffLocationData) throw new Error("Missing booking stops data");

  const { address: pickupAddress } = pickupLocationData;
  const { address: dropoffAddress } = dropoffLocationData;

  const showETA = !!driverCurrentLocationData && ETA;

  /*
   * A driver can be allocated before a vehicle is, and interpolating the make blind printed
   * "John Mock-Doe - undefined" when it wasn't. Rare while this page only opened mid-trip; normal now
   * that tracking is reachable from booking (DEV-1615), so the line is built from the parts that
   * actually exist and falls back to the driver's name alone.
   */
  const vehicleLabel = [
    driver?.vehicle?.model?.make,
    driver?.vehicle?.registrationNumber ? `(Plate: ${driver.vehicle.registrationNumber})` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card className="w-full border border-neutral-mid-gray p-0">
      <div className="rounded-t-lg border-b border-neutral-mid-gray bg-neutral-surface-gray px-5 py-4">
        <Typography variant="title">Booking Information</Typography>
      </div>
      <div className="space-y-4 rounded-b-lg bg-white p-5 pb-8">
        <div className="flex flex-row items-center gap-2.5">
          <Icon name="Calendar" size="lg" className="flex-shrink-0" />
          <Typography variant="paragraph">{formatInTimeZone(new Date(pickupDate), timezone, "dd-MM-yyyy")}</Typography>
        </div>
        <div className="flex flex-row items-center">
          <Icon name="Clock" size="lg" className="flex-shrink-0" />
          <Typography variant="paragraph" className="ml-2.5">
            {formatInTimeZone(new Date(pickupDate), timezone, "hh:mm a")}
          </Typography>
          <Tooltip content={format(new Date(pickupDate), "hh:mm a zzzz")} placement="bottom">
            <Icon name="InfoCircle" size="md" className="ml-1.5 flex-shrink-0 rotate-180" />
          </Tooltip>
        </div>
        {showETA && (
          <div className="flex flex-row items-center gap-2.5">
            <Icon name="Timer1" size="lg" className="flex-shrink-0" />
            <Typography variant="paragraph">{`ETA ${formatInTimeZone(
              addMinutes(new Date(driverCurrentLocationData.dateTime), ETA),
              timezone,
              "hh:mm a"
            )}`}</Typography>
          </div>
        )}
        <div className="flex flex-row gap-2.5">
          <Icon name="Location" size="lg" className="flex-shrink-0" />
          <Typography variant="paragraph">{pickupAddress}</Typography>
        </div>
        <div className="flex flex-row gap-2.5">
          <Icon name="Flag" size="lg" className="flex-shrink-0" />
          <Typography variant="paragraph">{dropoffAddress}</Typography>
        </div>
        {flightNumber && (
          <div className="flex flex-row items-center gap-2.5">
            <Icon name="Airplane" size="lg" className="flex-shrink-0" />
            <Typography variant="paragraph">{`Flight Number: ${flightNumber.toUpperCase()}`}</Typography>
          </div>
        )}
        {driver && (
          <div className="flex flex-row items-start gap-2.5">
            <Icon name="User" size="lg" className="flex-shrink-0" />
            <div className="flex flex-col">
              <Typography variant="paragraph">{vehicleLabel ? `${driver.name} - ${vehicleLabel}` : driver.name}</Typography>
              {/*
                The number is what someone standing at a terminal actually needs, and this page is opened
                on a phone, so it is a tel: link rather than text to copy out.

                Added to the payload by JobTripLogResource::getDriver (DEV-1615) — it was NOT there
                before, despite what RawBooking["driver"] claims. That type declares a phone the server
                never sent, which is why this rendered nothing until the backend caught up. The block
                already carried the driver's name, avatar, share code and rating, plus the OPERATOR's
                number under provider; the driver's own is the one new field, and it reaches the public
                trip-log link as well.
              */}
              {driver.phone && (
                <Typography variant="paragraph">
                  <a href={`tel:${driver.phone}`} className="text-info">
                    {driver.phone}
                  </a>
                </Typography>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-row items-center gap-2.5">
          <Icon name="Car" size="lg" className="flex-shrink-0" />
          <Typography variant="paragraph">{job.vehicleType}</Typography>
        </div>
        <div className="flex w-full justify-center">
          <Typography variant="action" className="mt-4 text-info">{`Job reference: J0${id}`}</Typography>
        </div>
      </div>
    </Card>
  );
};
